// Master Apps · Detalhe da aplicação.
// Tudo o que a tela mostra vem do cadastro: sem versões, hosts, changelog ou
// checklist ilustrativos. A conformidade é a regra única de @/lib/compliance,
// a mesma da auditoria e do acervo de documentos.
import { formatAppCode, horasDeProjeto } from '@masterapps/shared';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import {
  AppScreenshot,
  applicationImageUrls,
  CriticalityBadge,
  StatusBadge,
  VisibilityBadge,
} from '@/components/app-visuals';
import { DocDownloadButton } from '@/components/doc-download-button';
import {
  IconActivity,
  IconAlert,
  IconCheck,
  IconChevronR,
  IconEdit,
  IconExternal,
  IconLayers,
  IconLink,
  IconServer,
  IconShield,
  IconX,
} from '@/components/icons';
import { ButtonGhost, Card, Sidebar, StatusDot, Tag, TopBar } from '@/components/primitives';
import { useToggleFavorite } from '@/hooks/use-favorites';
import { byViewport, useViewport } from '@/hooks/use-viewport';
import type { App } from '@/lib/catalog-config';
import { APP_TYPES, STACK_COLORS, STATUS_TYPES } from '@/lib/catalog-config';
import type { ComplianceCheck, ComplianceKey, DocKey } from '@/lib/compliance';
import {
  COMPLIANCE_LEVEL_LABELS,
  complianceColor,
  complianceLevelColor,
  complianceOf,
  complianceTone,
  DOC_KEYS,
  docsFilled,
} from '@/lib/compliance';
import { DOC_FIELD_BY_KEY } from '@/lib/document-download';
import { formatDateBR, formatDateTimeBR } from '@/lib/format';
import type { ScreenProps } from '@/lib/theme';
import {
  fontMono,
  fontSans,
  makePalette,
  NOC_RED,
  numeroTabular,
  rotuloCaixaAlta,
  STATUS,
} from '@/lib/theme';

// ─── Grades, molduras e alvos de toque por faixa de viewport ───
// A ficha é 100% estilo inline, e estilo inline vence regra de classe: nenhuma
// `@media` em globals.css trocaria o `gridTemplateColumns` daqui sem `!important`
// em cada propriedade. Quem escolhe é `byViewport`, com as tabelas fora dos
// componentes — a decisão fica num lugar só e o objeto não é remontado a cada
// render.

/**
 * Corpo da ficha: conteúdo (2fr) + coluna lateral (1fr). O card "Acesso" da
 * lateral tem 228px de conteúdo mínimo (selo do ambiente + URL + ícone), então a
 * 768px a grade de duas colunas media 869px e vazava 101px da viewport. Abaixo do
 * desktop a lateral desce para baixo do conteúdo, em vez de disputar a largura.
 */
const BODY_GRID = { mobile: '1fr', tablet: '1fr', desktop: '2fr 1fr' } as const;

/**
 * Cards de documentação. Aos pares o bloco pedia 561px de largura mínima — mais
 * que a tela inteira de um celular. Um por linha a partir do celular.
 */
const DOC_GRID = { mobile: '1fr', tablet: '1fr 1fr', desktop: '1fr 1fr' } as const;

/** Mini tabela de ambientes · três caixas em linha só a partir do tablet. */
const ENV_GRID = { mobile: '1fr', tablet: 'repeat(3, 1fr)', desktop: 'repeat(3, 1fr)' } as const;

/**
 * Rótulo × valor dos metadados. A coluna fixa de 120px é 37% de um celular de
 * 320px: no compacto o rótulo passa a ficar ACIMA do valor.
 */
const META_GRID = { mobile: '1fr', tablet: '120px 1fr', desktop: '120px 1fr' } as const;
const META_GAP = { mobile: 3, tablet: 14, desktop: 14 } as const;
const META_ALIGN = { mobile: 'start', tablet: 'center', desktop: 'center' } as const;
/**
 * Valor de metadado sem espaço para quebrar (ID interno, slug): no celular pode
 * partir em qualquer ponto, senão um identificador de 36 caracteres vaza a
 * moldura. No desktop a propriedade fica no padrão do navegador.
 */
const META_BREAK = { mobile: 'anywhere', tablet: 'normal', desktop: 'normal' } as const;
/**
 * Piso de largura de item de grade. `undefined` no desktop de propósito: item de
 * grade tem `min-width: auto` (= min-content) por padrão, e emitir `0` lá mudaria
 * o cálculo das trilhas de hoje — o desenho do desktop tem de ficar igual.
 */
const SHRINK = { mobile: 0, tablet: 0, desktop: undefined } as const;

/** Respiro do conteúdo · 28px de cada lado custam 56 dos 320px de um celular. */
const HEAD_PAD = {
  mobile: '18px 14px 16px',
  tablet: '22px 20px 20px',
  desktop: '24px 28px 22px',
} as const;
const BODY_PAD = { mobile: 14, tablet: 18, desktop: 24 } as const;

/** Nome da aplicação · 30px em 292px úteis viram quatro linhas de título. */
const TITLE_SIZE = { mobile: 22, tablet: 26, desktop: 30 } as const;

/** Faixa da captura · 220px de altura tomam um quarto da tela de um celular. */
const SHOT_H = { mobile: 140, tablet: 180, desktop: 220 } as const;
/** Recuo dos selos sobre a captura · acompanha o respiro do conteúdo. */
const SHOT_INSET = { mobile: 12, tablet: 18, desktop: 24 } as const;
/**
 * No celular os selos sobrepostos ganham um limite à direita para não passarem
 * por baixo do botão de favorito (que é absoluto no canto oposto).
 */
const SHOT_BADGES_RIGHT = { mobile: 60, tablet: undefined, desktop: undefined } as const;

/**
 * Tira de miniaturas da galeria · o respiro horizontal acompanha o do bloco de
 * informações logo abaixo (HEAD_PAD), senão a tira "desalinharia" de tudo o que
 * vem depois dela.
 */
const STRIP_PAD = { mobile: '12px 14px', tablet: '14px 20px', desktop: '14px 28px' } as const;
/**
 * Miniatura · a 390px sobram 362px úteis, então três e meia aparecem na
 * primeira dobra — o meio corte é o que anuncia que a tira rola na horizontal.
 * Proporção ~3:2, a mesma que um recorte de tela costuma ter.
 */
const THUMB_W = { mobile: 92, tablet: 108, desktop: 120 } as const;
const THUMB_H = { mobile: 60, tablet: 70, desktop: 78 } as const;
/**
 * Convite da tira. Muda de verbo porque celular e tablet são tela de toque e o
 * desktop é ponteiro: "toque para ampliar" num monitor manda o leitor fazer algo
 * que ele não tem como fazer.
 */
const ZOOM_HINT = { mobile: 'toque para ampliar', desktop: 'clique para ampliar' } as const;
/** Botões do visualizador · 40px de alvo no toque, 34px no ponteiro. */
const VIEWER_BTN = { mobile: 40, tablet: 36, desktop: 34 } as const;
/**
 * Teto de altura da imagem ampliada. Desconta a barra de controles (~40px), o
 * gap e a margem que sobra em volta — é o que impede o diálogo, centralizado
 * por `translate(-50%, -50%)`, de nascer maior que a tela e sair pelas bordas.
 */
const VIEWER_IMG_H = {
  mobile: 'calc(100vh - 170px)',
  tablet: 'calc(100vh - 150px)',
  desktop: 'calc(100vh - 130px)',
} as const;

/** Selos e estatísticas em linha · embrulham onde a linha não cabe. */
const WRAP = { mobile: 'wrap', tablet: 'nowrap', desktop: 'nowrap' } as const;
/** Estatísticas rápidas · os três blocos pediam 311px dos 264 disponíveis. */
const STATS_WRAP = { mobile: 'wrap', tablet: 'wrap', desktop: 'nowrap' } as const;
// `'32px'` e não `32`: a tabela precisa ser homogênea para o genérico de
// `byViewport` inferir um tipo só, e o React já emitiria os mesmos 32px.
const STATS_GAP = { mobile: '14px 18px', tablet: '16px 24px', desktop: '32px' } as const;

/** Botão redondo de favorito · 36px é alvo de precisão para o dedo. */
const FAV_BTN = { mobile: 40, tablet: 40, desktop: 36 } as const;
/** Ação primária da barra superior ("Abrir aplicação") · 34px no desktop. */
const ACTION_H = { mobile: 40, tablet: 40, desktop: 34 } as const;

/** Ícone de cada artefato documental (a ordem vem de DOC_KEYS). */
const DOC_ICONS: Partial<Record<ComplianceKey, ReactNode>> = {
  tech: <IconShield size={16} />,
  biz: <IconActivity size={16} />,
  api: <IconLayers size={16} />,
  req: <IconCheck size={16} />,
  repo: <IconServer size={16} />,
};

/** Situação de um critério de conformidade, do ponto de vista da ficha. */
type CheckState = 'ok' | 'pendente' | 'dispensado';

// Dispensa vem antes de tudo: critério isento não é pendência nem mérito.
function checkStateOf(check: ComplianceCheck): CheckState {
  if (check.exempt) return 'dispensado';
  return check.ok ? 'ok' : 'pendente';
}

// Cor e selo por situação — tabela de decisão em vez de ternário aninhado.
// `null` = usa o cinza neutro do tema.
const CHECK_STATE_COLORS: Record<CheckState, string | null> = {
  ok: STATUS.ok!.solid,
  pendente: STATUS.high!.solid,
  dispensado: null,
};

const CHECK_STATE_BADGES: Record<CheckState, ReactNode> = {
  ok: (
    <>
      <IconCheck size={13} /> OK
    </>
  ),
  pendente: (
    <>
      <IconX size={13} /> Pendente
    </>
  ),
  dispensado: (
    <>
      <IconCheck size={13} /> Dispensado
    </>
  ),
};

/**
 * `execCommand` é obsoleto e está aqui de propósito: é o ÚNICO caminho de cópia
 * em contexto inseguro ou navegador antigo. O tipo local (em vez de
 * `document.execCommand`) declara o mínimo que usamos e trata a ausência do
 * método como "não copiou", em vez de depender de um TypeError.
 */
type LegacyCopyDocument = { execCommand?: (commandId: string) => boolean };

/**
 * Copia texto para a área de transferência. A Clipboard API só existe em
 * contexto seguro (HTTPS/localhost) e ainda pode ser negada por permissão;
 * quando falha, cai no truque do campo temporário + `execCommand('copy')`, que
 * funciona em navegador antigo e em HTTP interno. Devolve false quando nenhum
 * dos dois caminhos serviu, para o chamador poder mostrar a URL ao usuário.
 */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // permissão negada ou contexto inseguro — tenta o fallback abaixo
  }
  try {
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', 'true');
    field.style.position = 'fixed';
    field.style.top = '-1000px';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    field.setSelectionRange(0, text.length);
    const copied = (document as LegacyCopyDocument).execCommand?.('copy') ?? false;
    field.remove();
    return copied;
  } catch {
    return false;
  }
}

/** Host de uma URL cadastrada; valor intacto se não for uma URL válida. */
function hostOf(url: string): string {
  try {
    return new URL(url, window.location.href).hostname;
  } catch {
    return url;
  }
}

/**
 * Número no padrão pt-BR sem casa decimal fantasma: 8 sai "8" e 7,5 sai "7,5".
 * Fixar duas casas (como em formatUsdBR, onde centavo é obrigatório) escreveria
 * "8,00 h/dia" num campo que na prática quase sempre é inteiro.
 */
function numeroBR(valor: number): string {
  return valor.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}

/**
 * Esforço do bloco "Projeto" em uma linha legível. `null` quando nem os dias nem
 * as horas/dia foram informados — é o que apaga a linha inteira, em vez de um
 * "—" que o leitor confundiria com "não custou nada".
 *
 * Com as duas parcelas mostramos o total E a conta que o gerou: "120 h" sozinho
 * esconde se foram 15 dias folgados ou 3 corridos, e é justamente essa razão que
 * a área usa para comparar projetos. Com só uma das duas, mostramos a parcela
 * crua — arbitrar a outra metade (supor 8 h/dia) inventaria dado na ficha.
 */
function tempoDeProjeto(app: App): string | null {
  const dias = app.projetoDias;
  const horasDia = app.projetoHorasDia;
  const emDias =
    typeof dias === 'number' ? `${numeroBR(dias)} ${dias === 1 ? 'dia' : 'dias'}` : null;
  const porDia = typeof horasDia === 'number' ? `${numeroBR(horasDia)} h/dia` : null;
  if (!emDias && !porDia) return null;
  // O total é DERIVADO — a conta vive em @masterapps/shared porque é a mesma do
  // formulário. Reconferimos as duas parcelas aqui só porque o tipo `number |
  // null` do retorno não amarra, para o TypeScript, que ambas existem.
  const total = horasDeProjeto(app);
  if (total !== null && emDias && porDia) return `${numeroBR(total)} h (${emDias} × ${porDia})`;
  return emDias ?? porDia;
}

/**
 * Inteiro em pt-BR ("1.234") ou "—" quando não há número. Os acessos são
 * coletados automaticamente do Clerk de cada aplicação: `null` é "nunca
 * medido" (aplicação sem Clerk, coleta que não rodou), e não zero.
 */
function contagemBR(valor: number | null | undefined): string {
  return typeof valor === 'number' ? valor.toLocaleString('pt-BR') : '—';
}

/**
 * Bloco "Acessos nos últimos 30 dias" da ficha: sessões e usuários distintos
 * lidos do Clerk da aplicação, com o carimbo de quando a coleta rodou.
 *
 * Nada aqui vem do formulário — é o único bloco da ficha inteiramente
 * automático, e o rodapé diz isso para ninguém procurar onde editar. Três
 * estados: coletado (números + "coletado em"), coleta que falhou (números em
 * "—" e o erro, para o admin saber que não é "zero acesso") e nunca coletado
 * ("—" e o aviso). Sem `acessosColetadoEm` os números não se mostram mesmo que
 * existam: um número sem data é um número de quando?
 */
function AcessosCard({ app, dark }: Readonly<{ app: App; dark: boolean }>) {
  const p = makePalette(dark);
  const coletado = !!app.acessosColetadoEm && !app.acessosErro;
  const numeros = [
    { l: 'Acessos', v: coletado ? contagemBR(app.acessosMes) : '—' },
    { l: 'Usuários ativos', v: coletado ? contagemBR(app.acessosUsuariosMes) : '—' },
  ];
  let rodape: string;
  if (app.acessosErro) rodape = `Última coleta falhou: ${app.acessosErro}`;
  else if (coletado) rodape = `Coletado em ${formatDateTimeBR(app.acessosColetadoEm)}`;
  else rodape = 'Ainda não coletado: aplicação sem Clerk configurado ou coleta não executada.';

  return (
    <Card
      dark={dark}
      title="Acessos nos últimos 30 dias"
      subtitle="Lidos do Clerk da aplicação · visível apenas para administradores"
    >
      <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
        {numeros.map((n) => (
          <div key={n.l}>
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: p.muted,
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                marginBottom: 4,
              }}
            >
              {n.l}
            </div>
            <div
              style={{ ...numeroTabular, fontSize: 20, fontWeight: 700, color: p.text }}
              title={app.acessosErro ? `Última coleta falhou: ${app.acessosErro}` : undefined}
            >
              {n.v}
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          ...numeroTabular,
          marginTop: 12,
          fontSize: 11,
          lineHeight: 1.55,
          color: app.acessosErro ? STATUS.warning?.fg : p.muted,
          overflowWrap: 'anywhere',
        }}
      >
        {rodape}
      </div>
    </Card>
  );
}

function DocLinkCard({
  icon,
  label,
  url,
  dark,
  accent,
  exempt,
  download,
}: Readonly<{
  icon: ReactNode;
  label: ReactNode;
  url?: string | null;
  dark: boolean;
  accent: string;
  /** A aplicação é dispensada deste artefato (regra de @/lib/compliance). */
  exempt?: boolean;
  /** Presente (só para admin): habilita o botão de baixar via servidor. */
  download?: { applicationId: string; field: string };
}>) {
  const p = makePalette(dark);
  const isMissing = !url;
  // Sem link o card não pode virar âncora navegável: `href` fica ausente.
  const href = url || undefined;
  const iconBg = dark ? `${accent}22` : `${accent}15`;
  const missingIconBg = dark ? p.surface3 : p.surface2;
  const missingHint = exempt ? 'Dispensado para esta aplicação' : 'Não cadastrado';
  // A moldura é um <div> e a parte NAVEGÁVEL é a âncora interna: o botão de
  // baixar fica como IRMÃO dela (interativo dentro de interativo é HTML
  // inválido — mesma regra do card do catálogo com a estrela de favorito).
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '12px 14px',
        background: p.surface,
        border: `1px solid ${p.border}`,
        borderRadius: 8,
        opacity: isMissing ? 0.55 : 1,
        transition: 'border-color 160ms, background 160ms',
      }}
    >
      <a
        href={href}
        target={isMissing ? undefined : '_blank'}
        rel="noopener noreferrer"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flex: 1,
          minWidth: 0,
          textDecoration: 'none',
          cursor: isMissing ? 'default' : 'pointer',
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            background: isMissing ? missingIconBg : iconBg,
            color: isMissing ? p.muted : accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12.5,
              fontWeight: 600,
              color: p.text,
              marginBottom: 2,
            }}
          >
            {label}
            {exempt && (
              <span
                style={{
                  ...rotuloCaixaAlta,
                  fontSize: 9,
                  fontWeight: 600,
                  color: p.muted,
                  padding: '1px 5px',
                  borderRadius: 3,
                  background: p.surface2,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                dispensado
              </span>
            )}
          </div>
          <div
            style={{
              fontSize: 11,
              color: p.muted,
              fontFamily: fontMono,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {href ? hostOf(href) : missingHint}
          </div>
        </div>
        {!isMissing && (
          <span style={{ color: p.muted, display: 'flex', flexShrink: 0 }}>
            <IconExternal size={13} />
          </span>
        )}
      </a>
      {/* Admin-only: o chamador só passa `download` quando o papel permite; o
          próprio botão ainda se esconde quando o host não tem download direto. */}
      {!isMissing && download && (
        <DocDownloadButton
          applicationId={download.applicationId}
          field={download.field}
          url={url}
          dark={dark}
          accent={accent}
        />
      )}
    </div>
  );
}

function MetaRow({
  label,
  children,
  dark,
  valor,
}: Readonly<{
  label: ReactNode;
  children: ReactNode;
  dark: boolean;
  /**
   * Papel tipográfico do valor (ver @/lib/theme). `undefined` = texto comum na
   * sans. Antes isto era um `mono?: boolean` que misturava data com slug — só
   * o identificador é que continua monoespaçado.
   */
  valor?: 'numero' | 'identificador';
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: byViewport(vp, META_GRID),
        gap: byViewport(vp, META_GAP),
        padding: '10px 0',
        borderBottom: `1px solid ${p.border}`,
        alignItems: byViewport(vp, META_ALIGN),
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: p.muted,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          fontFamily: fontSans,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          color: p.text,
          ...(valor === 'numero' ? numeroTabular : {}),
          fontFamily: valor === 'identificador' ? fontMono : fontSans,
          minWidth: byViewport(vp, SHRINK),
          overflowWrap: byViewport(vp, META_BREAK),
        }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * Texto livre do bloco "Projeto" (hoje só o Retorno). Não usa
 * MetaRow porque isto é parágrafo, não valor curto: o rótulo sobe para o texto
 * ocupar a largura inteira do card em qualquer viewport (na coluna de 120px do
 * MetaRow uma resposta de 2000 caracteres viraria uma tira estreita).
 *
 * `pre-wrap` preserva as quebras de linha que a pessoa digitou e `anywhere`
 * parte URL ou palavra longa antes que ela vaze a moldura. Sem recorte nem "ver
 * mais": é a justificativa que o solicitante escreveu, e texto de justificativa
 * cortado pela metade é pior do que texto comprido.
 */
function ProjetoTexto({
  label,
  children,
  dark,
}: Readonly<{ label: ReactNode; children: string; dark: boolean }>) {
  const p = makePalette(dark);
  return (
    <div style={{ padding: '10px 0', borderBottom: `1px solid ${p.border}` }}>
      <div
        style={{
          fontSize: 10,
          color: p.muted,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          fontFamily: fontSans,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          color: p.text,
          lineHeight: 1.6,
          fontFamily: fontSans,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
        }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * Linha de URL do card "Acesso": selo do ambiente + URL completa + ícone de link
 * externo. `color` é a cor do selo — no tema escuro o fundo é a própria cor com
 * alfa 22, no claro é `lightBg`.
 */
/**
 * Ambiente NÃO cadastrado no card de acesso — tracejado informativo, sem link.
 * Extraído do antigo bloco inline da homologação porque a produção também
 * virou nullable (obrigatória só quando a situação é produção).
 */
function EnvUrlMissing({
  env,
  label,
  dark,
}: Readonly<{ env: string; label: string; dark: boolean }>) {
  const p = makePalette(dark);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '12px 14px',
        background: 'transparent',
        border: `1px dashed ${p.border}`,
        borderRadius: 7,
        fontSize: 11.5,
        color: p.muted,
        fontFamily: fontSans,
      }}
    >
      <span
        style={{
          ...rotuloCaixaAlta,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          padding: '3px 8px',
          borderRadius: 3,
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '0.1em',
          background: p.surface2,
          color: p.muted,
          textTransform: 'uppercase',
        }}
      >
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: p.muted }} />
        {/* Sigla como expressão (e não texto solto): deixa explícito que não há
            espaço entre o ponto e o rótulo — o respiro vem do `gap` do flex,
            como no selo do EnvUrlLink. */}
        {env}
      </span>
      <span style={{ flex: 1 }}>{label}</span>
    </div>
  );
}

function EnvUrlLink({
  url,
  env,
  color,
  lightBg,
  dark,
}: Readonly<{
  url: string;
  /** Sigla do ambiente no selo (PROD, HML). */
  env: string;
  color: string;
  lightBg: string;
  dark: boolean;
}>) {
  const p = makePalette(dark);
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '12px 14px',
        background: dark ? '#000' : p.surface2,
        border: `1px solid ${p.border}`,
        borderRadius: 7,
        textDecoration: 'none',
      }}
    >
      <span
        style={{
          ...rotuloCaixaAlta,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          padding: '3px 8px',
          borderRadius: 3,
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '0.1em',
          background: dark ? `${color}22` : lightBg,
          color,
          textTransform: 'uppercase',
          flexShrink: 0,
        }}
      >
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: color }} />
        {env}
      </span>
      <span
        style={{
          flex: 1,
          fontSize: 12,
          fontFamily: fontMono,
          color: p.text,
          wordBreak: 'break-all',
        }}
      >
        {url}
      </span>
      <span style={{ color: p.muted, flexShrink: 0 }}>
        <IconExternal size={13} />
      </span>
    </a>
  );
}

// Caixa de um ambiente na mini tabela do stack: link quando a URL está
// cadastrada, caixa tracejada (sem link) quando não está.
function EnvironmentBox({
  label,
  url,
  tone,
  dark,
}: Readonly<{ label: string; url: string | null; tone: string; dark: boolean }>) {
  const p = makePalette(dark);
  const missing = !url;
  const box = {
    padding: 12,
    background: missing ? 'transparent' : p.surface2,
    border: `1px ${missing ? 'dashed' : 'solid'} ${p.border}`,
    borderRadius: 6,
    display: 'block',
    textDecoration: 'none',
  } as const;
  const body = (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 6,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: missing ? p.muted : p.text,
            letterSpacing: '0.04em',
          }}
        >
          {label}
        </div>
        <StatusDot tone={missing ? 'unknown' : tone} size={6} />
      </div>
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 600,
          color: missing ? p.muted : p.text,
          fontFamily: fontMono,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {url ? hostOf(url) : 'Não cadastrado'}
      </div>
    </>
  );
  if (!url) return <div style={box}>{body}</div>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" style={box}>
      {body}
    </a>
  );
}

// ─── Galeria de telas ───
// A capa continua sendo o herói da ficha (AppScreenshot, intocado). A galeria é
// ADITIVA: só aparece quando o cadastro tem mais de uma imagem.

/**
 * Índice vizinho com volta nas pontas: da última imagem a seta para a direita
 * volta à primeira. Sem a volta seria preciso desabilitar um botão em cada
 * extremo, e com no máximo oito telas o gesto natural é varrer a sequência.
 */
function indiceVizinho(indice: number, passo: 1 | -1, total: number): number {
  return (indice + passo + total) % total;
}

/**
 * Botão do visualizador (anterior, próxima, fechar). Branco translúcido, e não
 * cor do tema: ele flutua sobre a imagem ampliada, que pode ser clara ou
 * escura — qualquer cor fixa da paleta sumiria em metade dos casos.
 */
function BotaoVisualizador({
  rotulo,
  tamanho,
  onClick,
  children,
}: Readonly<{ rotulo: string; tamanho: number; onClick: () => void; children: ReactNode }>) {
  return (
    <button
      type="button"
      title={rotulo}
      aria-label={rotulo}
      onClick={onClick}
      style={{
        width: tamanho,
        height: tamanho,
        padding: 0,
        borderRadius: 8,
        background: 'rgba(255,255,255,0.14)',
        border: '1px solid rgba(255,255,255,0.28)',
        color: '#fff',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}

/**
 * Uma miniatura da tira. `quebrada` vem de fora (a galeria guarda as URLs que
 * falharam) para a mesma imagem não tentar de novo no visualizador depois de já
 * ter falhado aqui — e, como o estado é por URL e não um booleano, trocar a
 * imagem no cadastro faz a miniatura voltar a tentar sozinha (mesmo padrão de
 * AppScreenshot).
 */
function GaleriaMiniatura({
  url,
  posicao,
  total,
  appName,
  dark,
  quebrada,
  onFalhar,
  onAbrir,
}: Readonly<{
  url: string;
  /** 1-based: é o que o rótulo lê em voz alta. */
  posicao: number;
  total: number;
  appName: string;
  dark: boolean;
  quebrada: boolean;
  onFalhar: () => void;
  onAbrir: () => void;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const rotulo = `Ampliar imagem ${posicao} de ${total} de ${appName}`;
  return (
    <button
      type="button"
      title={rotulo}
      aria-label={rotulo}
      onClick={onAbrir}
      style={{
        width: byViewport(vp, THUMB_W),
        height: byViewport(vp, THUMB_H),
        flexShrink: 0,
        padding: 0,
        borderRadius: 7,
        border: `1px solid ${p.border}`,
        background: p.surface2,
        overflow: 'hidden',
        // `zoom-in` é a única affordance possível sem :hover (estilo inline não
        // tem pseudoclasse) — e diz exatamente o que o clique faz.
        cursor: 'zoom-in',
        display: 'block',
      }}
    >
      {quebrada ? (
        <span
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            width: '100%',
            height: '100%',
            color: p.muted,
            fontFamily: fontSans,
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          <IconAlert size={14} />
          Indisponível
        </span>
      ) : (
        // `alt=""`: o botão já carrega o rótulo: com alt preenchido o leitor de
        // tela anunciaria a mesma imagem duas vezes.
        <img
          // Miniatura: a variante de 320 da mesma imagem que o visualizador abre em 1280.
          src={url.replace(/w=1280$/, 'w=320')}
          alt=""
          loading="lazy"
          decoding="async"
          onError={onFalhar}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            // Âncora no topo, como no herói: tela de sistema se reconhece pelo
            // cabeçalho, então o corte tira a parte de baixo.
            objectPosition: 'top',
            display: 'block',
          }}
        />
      )}
    </button>
  );
}

/**
 * Visualizador ampliado · portal em `document.body` com fundo escuro.
 *
 * `<dialog open>` e NÃO `showModal()`: a versão modal joga o elemento na top
 * layer e PRENDE o foco do teclado lá dentro. Aqui o foco continua na miniatura
 * que abriu — o Esc devolve o usuário exatamente ao ponto da ficha em que ele
 * estava, sem precisar guardar e restaurar foco, e nada do resto da página sai
 * do alcance do Tab.
 *
 * O fundo escuro é um IRMÃO abaixo no z-index (mesmo desenho do InfoPopover):
 * clique fora da imagem chega nele e fecha; clique na imagem ou nos controles
 * não, porque o diálogo está por cima. O diálogo tem a largura do CONTEÚDO
 * (`fit-content`), então não existe faixa "dentro do diálogo, fora da imagem"
 * engolindo o clique de fechar.
 */
function GaleriaVisualizador({
  url,
  indice,
  total,
  appName,
  quebrada,
  onFalhar,
  onFechar,
  onPasso,
}: Readonly<{
  url: string;
  /** 0-based (o contador soma 1 na exibição). */
  indice: number;
  total: number;
  appName: string;
  quebrada: boolean;
  onFalhar: () => void;
  onFechar: () => void;
  onPasso: (passo: 1 | -1) => void;
}>) {
  const vp = useViewport();
  const btn = byViewport(vp, VIEWER_BTN);
  return createPortal(
    <>
      {/* Fundo: fecha ao clicar fora. Decorativo, então `aria-hidden` e sem
          foco — um backdrop focável só criaria uma parada de tabulação
          invisível; quem usa teclado fecha no Esc. */}
      <div
        aria-hidden="true"
        onClick={onFechar}
        style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.8)' }}
      />
      <dialog
        open
        aria-label={`Telas de ${appName}`}
        style={{
          // As seis primeiras declarações neutralizam o stylesheet do
          // navegador para <dialog>: `margin: auto` + `inset` (que brigariam
          // com a centralização), `padding: 1em`, `border: solid`,
          // `background-color: Canvas`, `color: CanvasText` e `overflow: auto`.
          // `width: fit-content` é o do próprio navegador, repetido de
          // propósito: largura não-automática é o que faz o `right: 0` do UA
          // ser ignorado, em vez de esticar o diálogo até a borda da tela.
          margin: 0,
          padding: 0,
          border: 'none',
          background: 'transparent',
          color: '#fff',
          overflow: 'visible',
          width: 'fit-content',
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 9001,
          // Piso de largura: com uma imagem estreita (ou ainda carregando) a
          // barra de controles não pode espremer o contador e os botões.
          minWidth: 'min(280px, calc(100vw - 32px))',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          fontFamily: fontSans,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              ...numeroTabular,
              flex: 1,
              fontSize: 12,
              fontWeight: 700,
              color: '#fff',
              textShadow: '0 1px 3px rgba(0,0,0,0.6)',
            }}
          >
            {indice + 1} / {total}
          </span>
          <BotaoVisualizador rotulo="Imagem anterior" tamanho={btn} onClick={() => onPasso(-1)}>
            {/* Não existe IconChevronL no conjunto — o mesmo glifo virado 180°
                evita mexer em icons.tsx por uma seta. */}
            <IconChevronR size={16} style={{ transform: 'rotate(180deg)' }} />
          </BotaoVisualizador>
          <BotaoVisualizador rotulo="Próxima imagem" tamanho={btn} onClick={() => onPasso(1)}>
            <IconChevronR size={16} />
          </BotaoVisualizador>
          <BotaoVisualizador rotulo="Fechar (Esc)" tamanho={btn} onClick={onFechar}>
            <IconX size={16} />
          </BotaoVisualizador>
        </div>
        {quebrada ? (
          // Sem isto o <img> quebrado colapsaria para 0×0 e o diálogo sumiria
          // da tela com o fundo escuro ainda aceso — um beco sem saída visual.
          <div
            style={{
              width: 'min(420px, calc(100vw - 32px))',
              minHeight: 160,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: 20,
              borderRadius: 10,
              border: '1px dashed rgba(255,255,255,0.35)',
              background: 'rgba(255,255,255,0.06)',
              color: '#fff',
              fontSize: 12.5,
              lineHeight: 1.5,
              textAlign: 'center',
            }}
          >
            <IconAlert size={18} />
            Esta imagem não está disponível.
          </div>
        ) : (
          <img
            src={url}
            alt={`Imagem ${indice + 1} de ${total} de ${appName}`}
            decoding="async"
            onError={onFalhar}
            style={{
              display: 'block',
              // `contain` (e não `cover`): aqui o assunto é VER a tela inteira,
              // que é justamente o que o corte do herói não deixa.
              objectFit: 'contain',
              width: 'auto',
              height: 'auto',
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: byViewport(vp, VIEWER_IMG_H),
              borderRadius: 10,
              background: '#000',
              boxShadow: '0 24px 60px -20px rgba(0,0,0,0.75)',
            }}
          />
        )}
      </dialog>
    </>,
    document.body,
  );
}

/**
 * Galeria da ficha: tira de miniaturas sob a capa + visualizador ampliado.
 *
 * POR QUE TIRA, E NÃO CARROSSEL NO LUGAR DA CAPA: a capa é a identidade da
 * aplicação — catálogo, deck e ficha mostram a MESMA imagem. Trocar o herói por
 * um carrossel faria a ficha exibir outra imagem a cada visita e mexeria no
 * desenho das 44 aplicações que só têm capa. A tira é aditiva: quem tem uma
 * imagem só nunca a vê (quem decide montar é o chamador, com `urls.length > 1`)
 * e quem tem oito vê todas de uma vez, sem esperar animação nem descobrir que
 * existe um "próximo".
 *
 * A CAPA ENTRA COMO PRIMEIRA MINIATURA de propósito: `applicationImageUrls` é a
 * sequência inteira, o contador do visualizador bate com o que a pessoa vê na
 * tira, e o herói corta a imagem (`objectFit: cover`) — clicar na primeira
 * miniatura é a única forma de ver a capa completa.
 */
function AppGaleria({
  urls,
  appName,
  dark,
}: Readonly<{ urls: readonly string[]; appName: string; dark: boolean }>) {
  const p = makePalette(dark);
  const vp = useViewport();
  // Um estado para "aberto" e "qual": índice no visualizador, `null` fechado.
  const [aberta, setAberta] = useState<number | null>(null);
  // URLs que falharam (e não um booleano por posição): a chave é a própria URL,
  // então trocar a imagem no cadastro faz a ficha voltar a tentar — mesmo
  // raciocínio do `failedUrl` de AppScreenshot. Lista, e não Set, porque são no
  // máximo oito itens e estado imutável cai melhor no React.
  const [falharam, setFalharam] = useState<readonly string[]>([]);
  const total = urls.length;
  const aberto = aberta !== null;
  const urlAberta = aberta === null ? null : urls[aberta];

  const marcarFalha = (falha: string) =>
    setFalharam((atual) => (atual.includes(falha) ? atual : [...atual, falha]));
  const fechar = useCallback(() => setAberta(null), []);
  const passar = useCallback(
    (passo: 1 | -1) =>
      setAberta((atual) => (atual === null ? null : indiceVizinho(atual, passo, total))),
    [total],
  );

  // Esc fecha, setas andam pela sequência. O listener é de `window` (e não do
  // diálogo) JUSTAMENTE porque o foco não é preso lá dentro: ele continua na
  // miniatura que abriu o visualizador, e um handler no diálogo nunca veria a
  // tecla. Só vale com o visualizador aberto — fechado, a ficha não sequestra
  // seta nenhuma.
  useEffect(() => {
    if (!aberto) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        fechar();
        return;
      }
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      // Sem o preventDefault a seta ainda rolaria a ficha por baixo do fundo.
      e.preventDefault();
      passar(e.key === 'ArrowRight' ? 1 : -1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [aberto, fechar, passar]);

  return (
    <>
      <div
        style={{
          padding: byViewport(vp, STRIP_PAD),
          background: p.surface,
          borderBottom: `1px solid ${p.border}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            gap: 8,
            marginBottom: 8,
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: p.muted,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontFamily: fontSans,
            }}
          >
            Telas
          </span>
          {/* A dica fica escrita, e não só no `title` da miniatura: no celular
              não existe passar o mouse para descobrir que dá para ampliar. */}
          <span style={{ fontSize: 11, color: p.muted, fontFamily: fontSans }}>
            <span style={numeroTabular}>{total}</span> imagens · {byViewport(vp, ZOOM_HINT)}
          </span>
        </div>
        {/* Rola na horizontal em vez de embrulhar: a tira é uma faixa só, e o
            corte da última miniatura visível é o que avisa que há mais. */}
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 2 }}>
          {urls.map((url, i) => (
            <GaleriaMiniatura
              key={url}
              url={url}
              posicao={i + 1}
              total={total}
              appName={appName}
              dark={dark}
              quebrada={falharam.includes(url)}
              onFalhar={() => marcarFalha(url)}
              onAbrir={() => setAberta(i)}
            />
          ))}
        </div>
      </div>

      {aberta !== null && urlAberta && (
        <GaleriaVisualizador
          url={urlAberta}
          indice={aberta}
          total={total}
          appName={appName}
          quebrada={falharam.includes(urlAberta)}
          onFalhar={() => marcarFalha(urlAberta)}
          onFechar={fechar}
          onPasso={passar}
        />
      )}
    </>
  );
}

export function ScreenAppDetail({
  dark,
  accent = NOC_RED,
  app,
  role = 'admin',
}: Readonly<ScreenProps & { app: App; role?: 'admin' | 'collab' }>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const navigate = useNavigate();
  // Favorito por usuário: mesma mutation dos cards do catálogo (optimistic
  // update no cache, então a estrela responde no clique).
  const toggleFavorite = useToggleFavorite();
  const favorite = !!app.favorite;
  const favoriteToggleLabel = favorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos';
  const favoriteLabel = toggleFavorite.isPending ? 'Salvando favorito…' : favoriteToggleLabel;

  // "Compartilhar" copia o link desta tela. Se nem a Clipboard API nem o
  // fallback funcionarem, mostramos a URL para o usuário copiar na mão.
  const onShare = async () => {
    const url = window.location.href;
    if (await copyText(url)) {
      toast.success('Link copiado');
      return;
    }
    if (typeof window.prompt === 'function') {
      window.prompt('Copie o link desta aplicação:', url);
      return;
    }
    toast.error(`Não foi possível copiar. O link é ${url}`);
  };

  const compliance = complianceOf(app);
  const docChecks = compliance.checks.filter((c) => c.kind === 'doc');
  const levelColor = complianceLevelColor(compliance.level);
  const levelTone = complianceTone(levelColor, dark);

  // Bloco "Projeto": opcional campo a campo, e o card inteiro só existe se pelo
  // menos um dos cinco foi preenchido. Desenhá-lo vazio nas 44 fichas anteriores
  // à seção afirmaria que a área deixou de responder, quando a pergunta nem
  // existia no cadastro delas — e uma fileira de "—" é ruído em toda ficha
  // antiga. Sem recorte por papel: é a descrição do projeto (não custo), então
  // vale para quem já pode abrir a ficha.
  const tempoProjeto = tempoDeProjeto(app);
  const temProjeto = !!(tempoProjeto || app.projetoRetorno || app.projetoArea || app.projetoDono);

  // Sequência completa de imagens (capa + telas adicionais). Com uma imagem só
  // — o caso das 44 aplicações de hoje — ou nenhuma, a ficha não ganha nada:
  // o herói continua sozinho, exatamente como era antes da galeria existir.
  // 1280 é o que o visualizador mostra; a tira de miniaturas troca por 320
  // (ver `GaleriaMiniatura`) — ninguém aqui baixa o original.
  const galeria = applicationImageUrls(app, 1280);

  // Ambientes: um por URL cadastrada. Sem coluna de versão, mostramos o host.
  const environments: { label: string; url: string | null; tone: string }[] = [
    { label: 'Produção', url: app.url, tone: 'ok' },
    { label: 'Homologação', url: app.urlHml, tone: 'warning' },
    { label: 'Desenvolvimento', url: app.urlDev, tone: 'info' },
  ];

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        fontFamily: fontSans,
        color: p.text,
        background: p.bg,
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      <Sidebar active="inventory" dark={dark} accent={accent} role={role} />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TopBar
          dark={dark}
          breadcrumb={['Administração', 'Inventário']}
          title={app.name}
          subtitle={`${app.team} · ${app.owner}`}
          right={
            <>
              {/* Editar antes de Compartilhar (pedido de produto): admin corrige
                  o cadastro direto da ficha, sem passar pelo Inventário. */}
              {role === 'admin' && (
                <ButtonGhost
                  dark={dark}
                  onClick={() => navigate(`/admin/applications/${app.slug}/edit`)}
                >
                  <IconEdit size={13} /> Editar
                </ButtonGhost>
              )}
              <ButtonGhost dark={dark} onClick={() => void onShare()}>
                <IconLink size={13} /> Compartilhar
              </ButtonGhost>
              {/* A URL de produção é nullable (obrigatória só quando a situação
                  é produção): o CTA abre o melhor ambiente existente e some
                  quando a aplicação ainda não está no ar em lugar nenhum. */}
              {(app.url ?? app.urlHml ?? app.urlDev) && (
                <a
                  href={app.url ?? app.urlHml ?? app.urlDev ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    height: byViewport(vp, ACTION_H),
                    padding: '0 14px',
                    background: accent,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 7,
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: fontSans,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    textDecoration: 'none',
                  }}
                >
                  Abrir aplicação <IconExternal size={13} />
                </a>
              )}
            </>
          }
        />

        <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {/* Imagem panorâmica de destaque */}
          <div style={{ position: 'relative' }}>
            <AppScreenshot app={app} dark={dark} env="prod" height={byViewport(vp, SHOT_H)} />
            {/* badges sobrepostos */}
            <div
              style={{
                position: 'absolute',
                top: 16,
                left: byViewport(vp, SHOT_INSET),
                // Teto à direita só no celular: sem ele os dois selos passavam por
                // baixo do botão de favorito, que é absoluto no canto oposto.
                right: byViewport(vp, SHOT_BADGES_RIGHT),
                display: 'flex',
                alignItems: 'center',
                flexWrap: byViewport(vp, WRAP),
                gap: 8,
              }}
            >
              <span
                style={{
                  ...rotuloCaixaAlta,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '4px 9px',
                  borderRadius: 4,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  background: 'rgba(255,255,255,0.95)',
                  color: APP_TYPES[app.type]?.color || '#475569',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                }}
              >
                <span
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    background: APP_TYPES[app.type]?.color || '#475569',
                  }}
                />
                {APP_TYPES[app.type]?.label}
              </span>
              <span
                style={{
                  ...rotuloCaixaAlta,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '4px 9px',
                  borderRadius: 4,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  background: 'rgba(0,0,0,0.65)',
                  color: '#fff',
                  backdropFilter: 'blur(4px)',
                }}
              >
                {STATUS_TYPES[app.status]?.label ?? app.status}
              </span>
            </div>
            <div
              style={{
                position: 'absolute',
                top: 16,
                right: byViewport(vp, SHOT_INSET),
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <button
                type="button"
                title={favoriteLabel}
                aria-label={favoriteLabel}
                aria-pressed={favorite}
                disabled={toggleFavorite.isPending}
                onClick={() => toggleFavorite.mutate({ id: app.id, favorite: !favorite })}
                style={{
                  width: byViewport(vp, FAV_BTN),
                  height: byViewport(vp, FAV_BTN),
                  borderRadius: 8,
                  background: 'rgba(255,255,255,0.92)',
                  color: favorite ? '#f59e0b' : '#78716c',
                  border: 'none',
                  cursor: toggleFavorite.isPending ? 'progress' : 'pointer',
                  opacity: toggleFavorite.isPending ? 0.65 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill={favorite ? '#f59e0b' : 'none'}
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              </button>
            </div>
          </div>

          {/* Telas adicionais · tira logo abaixo da capa, colada no bloco de
              informações (mesmo `surface`) para ler como o topo dele, e não
              como uma segunda faixa solta. Sem a segunda imagem não existe tira
              nenhuma: nada de moldura vazia nem controle órfão. */}
          {galeria.length > 1 && <AppGaleria urls={galeria} appName={app.name} dark={dark} />}

          {/* INFORMAÇÕES PRINCIPAIS */}
          <div
            style={{
              padding: byViewport(vp, HEAD_PAD),
              borderBottom: `1px solid ${p.border}`,
              background: p.surface,
            }}
          >
            {/* Quatro selos numa linha de 292px úteis vazavam 19px: no celular
                eles embrulham para a linha de baixo. */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: byViewport(vp, WRAP),
                gap: 10,
                marginBottom: 10,
              }}
            >
              <VisibilityBadge visibility={app.visibility} dark={dark} />
              <StatusBadge status={app.status} dark={dark} />
              <CriticalityBadge level={app.criticality} dark={dark} />
              {app.lgpd && (
                <span
                  style={{
                    ...rotuloCaixaAlta,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    color: '#2563eb',
                    padding: '2px 7px',
                    borderRadius: 3,
                    background: dark ? '#2563eb22' : '#dbeafe',
                  }}
                >
                  LGPD
                </span>
              )}
            </div>
            <div
              style={{
                fontSize: byViewport(vp, TITLE_SIZE),
                fontWeight: 700,
                color: p.text,
                letterSpacing: '-0.024em',
                lineHeight: 1.1,
                marginBottom: 12,
              }}
            >
              {app.name}
            </div>
            <div
              style={{
                fontSize: 14,
                color: p.textMuted,
                lineHeight: 1.6,
                maxWidth: 820,
              }}
            >
              {app.description}
            </div>

            {/* Estatísticas rápidas · os três blocos em linha pediam 311px dos
                264 que sobram a 320px, então no compacto eles embrulham. */}
            <div
              style={{
                display: 'flex',
                flexWrap: byViewport(vp, STATS_WRAP),
                gap: byViewport(vp, STATS_GAP),
                marginTop: 20,
                paddingTop: 16,
                borderTop: `1px solid ${p.border}`,
              }}
            >
              {[
                { l: 'Em prod desde', v: formatDateBR(app.deployedAt) || 'Não informado' },
                { l: 'Última atualização', v: formatDateBR(app.lastUpdate) },
                { l: 'Responsável', v: app.owner, nome: true },
              ].map((s) => (
                <div key={s.l}>
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: p.muted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.12em',
                      marginBottom: 4,
                    }}
                  >
                    {s.l}
                  </div>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: p.text,
                      ...numeroTabular,
                      letterSpacing: s.nome ? '-0.01em' : '-0.02em',
                    }}
                  >
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CORPO */}
          <div
            style={{
              padding: byViewport(vp, BODY_PAD),
              display: 'grid',
              gridTemplateColumns: byViewport(vp, BODY_GRID),
              gap: 20,
            }}
          >
            {/* Coluna esquerda */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Projeto · primeiro card da coluna larga, logo abaixo da
                  descrição: é o PORQUÊ da aplicação existir, contexto de leitura
                  para a documentação e o stack que vêm depois. Coluna larga (2fr)
                  porque o Retorno é parágrafo livre (até 2.000 caracteres). */}
              {temProjeto && (
                <Card
                  dark={dark}
                  title="Projeto"
                  subtitle="O ponto de vista de quem pediu a aplicação"
                >
                  <div>
                    {tempoProjeto && (
                      <MetaRow label="Tempo de projeto" dark={dark} valor="numero">
                        {tempoProjeto}
                      </MetaRow>
                    )}
                    {app.projetoRetorno && (
                      <ProjetoTexto label="Retorno" dark={dark}>
                        {app.projetoRetorno}
                      </ProjetoTexto>
                    )}
                    {app.projetoArea && (
                      <MetaRow label="Área" dark={dark}>
                        {app.projetoArea}
                      </MetaRow>
                    )}
                    {app.projetoDono && (
                      <MetaRow label="Dono do projeto" dark={dark}>
                        {app.projetoDono}
                      </MetaRow>
                    )}
                  </div>
                </Card>
              )}

              {/* Documentação */}
              <Card
                dark={dark}
                title="Documentação"
                subtitle="Links externos · abrem em nova aba"
                action={
                  <Tag dark={dark}>
                    {docsFilled(app)}/{DOC_KEYS.length} cadastrados
                  </Tag>
                }
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: byViewport(vp, DOC_GRID),
                    gap: 10,
                  }}
                >
                  {docChecks.map((c) => (
                    <DocLinkCard
                      key={c.key}
                      icon={DOC_ICONS[c.key]}
                      label={c.label}
                      url={c.value}
                      exempt={c.exempt}
                      dark={dark}
                      accent={accent}
                      // Download via servidor é recurso de ADMIN (a rota também
                      // barra no back — aqui é só não desenhar o botão).
                      download={
                        role === 'admin' && c.key in DOC_FIELD_BY_KEY
                          ? { applicationId: app.id, field: DOC_FIELD_BY_KEY[c.key as DocKey] }
                          : undefined
                      }
                    />
                  ))}
                </div>
              </Card>

              {/* Stack técnico */}
              <Card
                dark={dark}
                title="Stack técnico"
                subtitle={`${app.stacks.length} tecnologias em uso`}
              >
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                  }}
                >
                  {app.stacks.map((s) => (
                    <span
                      key={s}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '7px 12px',
                        borderRadius: 6,
                        fontSize: 12.5,
                        fontWeight: 500,
                        background: p.surface2,
                        color: p.text,
                        border: `1px solid ${p.border}`,
                        fontFamily: fontSans,
                      }}
                    >
                      <span
                        style={{
                          width: 9,
                          height: 9,
                          borderRadius: 2,
                          background: STACK_COLORS[s] || '#475569',
                        }}
                      />
                      {s}
                    </span>
                  ))}
                </div>

                {/* Mini tabela de ambientes */}
                <div style={{ marginTop: 18 }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: p.muted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      marginBottom: 8,
                    }}
                  >
                    Ambientes
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: byViewport(vp, ENV_GRID),
                      gap: 10,
                    }}
                  >
                    {environments.map((env) => (
                      <EnvironmentBox
                        key={env.label}
                        label={env.label}
                        url={env.url}
                        tone={env.tone}
                        dark={dark}
                      />
                    ))}
                  </div>
                </div>
              </Card>

              {/* Datas do cadastro — o que existe de histórico hoje */}
              <Card
                dark={dark}
                title="Datas do cadastro"
                subtitle="O que o inventário registra sobre esta aplicação"
              >
                <div>
                  <MetaRow label="Cadastrado em" dark={dark} valor="numero">
                    {formatDateBR(app.createdAt) || 'Não informado'}
                  </MetaRow>
                  <MetaRow label="Cadastro alterado" dark={dark} valor="numero">
                    {formatDateBR(app.updatedAt) || 'Não informado'}
                  </MetaRow>
                  <MetaRow label="Última atualização" dark={dark} valor="numero">
                    {formatDateBR(app.lastUpdate) || 'Não informado'}
                  </MetaRow>
                  <MetaRow label="Em produção desde" dark={dark} valor="numero">
                    {formatDateBR(app.deployedAt) || 'Não cadastrado'}
                  </MetaRow>
                </div>
                <div
                  style={{
                    marginTop: 12,
                    fontSize: 11,
                    color: p.muted,
                    lineHeight: 1.55,
                  }}
                >
                  O histórico detalhado de alterações não é registrado: o cadastro guarda apenas
                  estas datas.
                </div>
              </Card>
            </div>

            {/* Coluna direita */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* URL */}
              <Card dark={dark} title="Acesso" subtitle="URLs por ambiente · abrem em nova aba">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {/* Produção — nullable: obrigatória só quando a situação é
                      produção (missingStatusUrl); sem ela, o mesmo tracejado
                      informativo da homologação. */}
                  {app.url ? (
                    <EnvUrlLink
                      url={app.url}
                      env="PROD"
                      color="#16a34a"
                      lightBg="#dcfce7"
                      dark={dark}
                    />
                  ) : (
                    <EnvUrlMissing env="PROD" label="Sem ambiente de produção" dark={dark} />
                  )}
                  {/* Homologação */}
                  {app.urlHml ? (
                    <EnvUrlLink
                      url={app.urlHml}
                      env="HML"
                      color="#ca8a04"
                      lightBg="#fef9c3"
                      dark={dark}
                    />
                  ) : (
                    <EnvUrlMissing env="HML" label="Sem ambiente de homologação" dark={dark} />
                  )}
                </div>
              </Card>

              {/* Acessos · SÓ admin, a mesma regra do custo no catálogo: uso da
                  aplicação é dado de gestão, e a ficha é aberta por qualquer
                  colaborador com acesso à aplicação. */}
              {role === 'admin' && <AcessosCard app={app} dark={dark} />}

              {/* Identificação */}
              <Card dark={dark} title="Identificação" subtitle="Rastreabilidade interna">
                <div>
                  <MetaRow label="Chamado" dark={dark} valor="identificador">
                    {app.ticket && app.ticket !== '—' ? app.ticket : 'Não informado'}
                  </MetaRow>
                  <MetaRow label="Projeto" dark={dark} valor="identificador">
                    {app.project}
                  </MetaRow>
                  <MetaRow label="ID interno" dark={dark} valor="identificador">
                    {formatAppCode(app.code)}
                  </MetaRow>
                  <MetaRow label="Identificador (slug)" dark={dark} valor="identificador">
                    {app.slug}
                  </MetaRow>
                  <MetaRow label="Time" dark={dark}>
                    {app.team}
                  </MetaRow>
                  <MetaRow label="Responsável" dark={dark}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                      }}
                    >
                      {app.owner}
                      {app.ownerInactive && (
                        <span
                          title="O responsável cadastrado não consta ativo no RH"
                          style={{
                            ...rotuloCaixaAlta,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            padding: '2px 8px',
                            borderRadius: 999,
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            lineHeight: 1,
                            background: dark ? '#2a1212' : '#fef2f2',
                            color: dark ? '#fca5a5' : '#b91c1c',
                            border: `1px solid ${dark ? '#7f1d1d' : '#fecaca'}`,
                          }}
                        >
                          <IconAlert size={11} /> Desativado no RH
                        </span>
                      )}
                    </span>
                  </MetaRow>
                  {app.ownerInactive && (
                    <div
                      style={{
                        marginTop: 8,
                        padding: '9px 12px',
                        borderRadius: 7,
                        fontSize: 12,
                        lineHeight: 1.55,
                        background: dark ? '#2a1212' : '#fef2f2',
                        color: dark ? '#fca5a5' : '#991b1b',
                        border: `1px solid ${dark ? '#7f1d1d' : '#fecaca'}`,
                      }}
                    >
                      O responsável desta aplicação está desativado no RH — atualize o responsável
                      no cadastro para manter a rastreabilidade da auditoria.
                    </div>
                  )}
                </div>
              </Card>

              {/* Compliance — regra única de @/lib/compliance */}
              <Card
                dark={dark}
                title="Compliance · Auditoria"
                subtitle="Critérios da auditoria 2026"
                action={
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '2px 6px',
                      borderRadius: 3,
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      fontFamily: fontSans,
                      background: levelTone.bg,
                      color: levelTone.fg,
                      border: `1px solid ${levelTone.border}`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {COMPLIANCE_LEVEL_LABELS[compliance.level]}
                  </span>
                }
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {compliance.checks.map((c) => {
                    const state = checkStateOf(c);
                    return (
                      <div
                        key={c.key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 10,
                          fontSize: 12.5,
                          color: state === 'dispensado' ? p.textMuted : p.text,
                          padding: '6px 0',
                        }}
                      >
                        <span>{c.label}</span>
                        <span
                          style={{
                            color: CHECK_STATE_COLORS[state] ?? p.muted,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 11,
                            fontWeight: 600,
                            fontFamily: fontSans,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {CHECK_STATE_BADGES[state]}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div
                  style={{
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: `1px solid ${p.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: 12,
                  }}
                >
                  <span style={{ color: p.muted }}>Cobertura</span>
                  <span
                    style={{
                      ...numeroTabular,
                      fontWeight: 700,
                      color: complianceColor(compliance.pct),
                    }}
                  >
                    {compliance.ok} / {compliance.total} · {compliance.pct.toFixed(1)}%
                  </span>
                </div>
                {compliance.missing.length > 0 && (
                  <div
                    style={{
                      marginTop: 8,
                      fontSize: 11,
                      color: p.muted,
                      lineHeight: 1.55,
                    }}
                  >
                    Pendente: {compliance.missing.map((c) => c.label).join(', ')}.
                  </div>
                )}
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
